import {
  Button,
  DialogActions,
  DialogContent,
  Stack,
  SxProps,
  Typography,
  CircularProgress,
} from '@mui/material'
import { Box } from '@mui/system'
import { ReactElement } from 'react'
import { useTranslation } from '@/hooks/useTranslation'

interface ConfirmModalUIProps {
  title?: string
  /** What is about to be deleted, shown above the warning so it can be checked. */
  name?: string
  text?: string
  cancelText?: string
  confirmText?: string
  onClose?: () => void
  onConfirm?: () => void
  children?: ReactElement
  sx?: SxProps
  isLoading?: boolean
}
const ConfirmModalUI = ({
  title,
  name,
  text,
  cancelText,
  confirmText,
  onClose,
  onConfirm,
  children,
  sx,
  isLoading = false,
}: ConfirmModalUIProps) => {
  const { t } = useTranslation()
  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        ...sx,
      }}
    >
      {title && (
        <Stack sx={{ padding: '41px 152px 4px 34px' }}>
          <Typography
            sx={{
              color: 'text.main',
              fontWeight: 400,
              fontSize: '20px',
              lineHeight: '24px',
              minHeight: '24px',
              minWidth: '192px',
            }}
          >
            {title}
          </Typography>
        </Stack>
      )}
      <DialogContent
        sx={{ pt: '5px', paddingLeft: '34px', paddingBottom: '18px' }}
      >
        {name && (
          <Typography
            sx={{
              color: 'text.main',
              fontWeight: 700,
              fontSize: '14px',
              lineHeight: '1.3',
              maxWidth: '314px',
              marginBottom: '6px',
              // A name can be long and has no spaces to break on, so it wraps
              // mid-word rather than widening the dialog.
              overflowWrap: 'anywhere',
            }}
          >
            {name}
          </Typography>
        )}
        {text && (
          <Typography
            sx={{
              color: 'text.main',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '1.3',
              maxWidth: '314px',
              minHeight: '45px',
            }}
          >
            {text}
          </Typography>
        )}
        {children && children}
      </DialogContent>
      <DialogActions sx={{ paddingBottom: '32px', paddingRight: '30px' }}>
        {onClose && (
          <Button
            variant="secondary"
            sx={{ width: '99px' }}
            onClick={() => onClose()}
            disabled={isLoading}
          >
            {cancelText || t('commonActionsCancel')}
          </Button>
        )}
        {onConfirm && (
          <Button variant="primary" sx={{ width: '99px' }} onClick={onConfirm}>
            {isLoading ? (
              <CircularProgress size={20} sx={{ color: '#fff' }} />
            ) : (
              confirmText || t('commonActionsConfirm')
            )}
          </Button>
        )}
      </DialogActions>
    </Box>
  )
}

export default ConfirmModalUI
